import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  environmentLabel,
  inferenceModeLabel,
  modalityLabel,
  providerLabel,
  taskLabel,
} from '../aiModelConstants';
import { parseTags } from '../aiModelForm';
import { Field, inputClass } from '../aiModelUi';
import { StepProps } from './stepTypes';

const ReviewStep: React.FC<StepProps> = ({ form, isLight, patch }) => {
  const [advancedOpen, setAdvancedOpen] = useState(
    Boolean(form.framework || form.algorithm || form.components.length)
  );

  const rows: { label: string; value: string }[] = [
    { label: 'Name', value: form.name || '—' },
    { label: 'Code', value: form.code || '—' },
    { label: 'Version', value: form.version || '—' },
    { label: 'Environment', value: environmentLabel(form.environment) },
    { label: 'Provider', value: providerLabel(form.provider) },
    { label: 'Endpoint', value: form.endpointId || '—' },
    { label: 'Auth', value: form.authLabel || 'Not set' },
    { label: 'Task', value: taskLabel(form.task) },
    { label: 'Modality', value: modalityLabel(form.modality) },
    { label: 'Inference', value: inferenceModeLabel(form.inferenceMode) },
    { label: 'Inputs', value: String(form.inputFields.filter((f) => f.name.trim()).length) },
    { label: 'Outputs', value: String(form.outputFields.filter((f) => f.name.trim()).length) },
    {
      label: 'Preprocessing',
      value: form.preprocessingIncluded ? 'Included in deployment' : form.preprocessingNotes || 'Caller-side',
    },
    { label: 'Credentials', value: form.credentialsConfigured ? 'Stored (leave secret fields blank to keep)' : 'Entered on Source' },
    { label: 'Last test', value: form.lastTest ? `${form.lastTest.success ? 'Passed' : 'Incomplete'} · ${form.lastTest.latencyMs} ms` : 'Not run' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-3 text-xs">
            <span className={isLight ? 'text-neutral-500' : 'text-gray-400'}>{row.label}</span>
            <span className={`text-right ${isLight ? 'text-neutral-800' : 'text-gray-100'}`}>{row.value}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setAdvancedOpen((open) => !open)}
        className={`flex items-center gap-1 text-[11px] font-medium ${isLight ? 'text-neutral-600' : 'text-gray-300'}`}
      >
        {advancedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Advanced — framework / algorithm / components
      </button>

      {advancedOpen && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Framework" isLight={isLight}>
            <input
              value={form.framework}
              onChange={(e) => patch({ framework: e.target.value })}
              placeholder="XGBoost"
              className={inputClass(isLight)}
            />
          </Field>
          <Field label="Algorithm" isLight={isLight}>
            <input
              value={form.algorithm}
              onChange={(e) => patch({ algorithm: e.target.value })}
              placeholder="Gradient Boosted Trees"
              className={inputClass(isLight)}
            />
          </Field>
          <Field label="Components" hint="Comma-separated" isLight={isLight}>
            <input
              value={form.components.join(', ')}
              onChange={(e) => patch({ components: parseTags(e.target.value) })}
              placeholder="Predictor"
              className={inputClass(isLight)}
            />
          </Field>
        </div>
      )}
    </div>
  );
};

export default ReviewStep;
